import { useClaim } from '~/components/ClaimContainer';
import useDataFetcher from '~/components/useDataFetcher';
import type { ClaimMilestonesResponse } from '~/server_shared/openapi/data-contracts';
import { reportAxiosError } from '~/Utils';

import { clearClaimMilestone, getClaimMilestonesUrl, recordClaimMilestone } from '../apiService';

const useClaimMilestones = () => {
  const { claim, asyncRefreshData } = useClaim({ claimRequired: true });
  const { data, isLoading, isError, reloadData } = useDataFetcher<ClaimMilestonesResponse>(
    getClaimMilestonesUrl(claim.organization_id, claim.id)
  );

  const runMutation = async (mutation: () => Promise<void>) => {
    try {
      await mutation();
      // Every change is noted on the claim, and the Notes tab reloads its notes with the claim.
      asyncRefreshData();
    } catch (error) {
      reportAxiosError(error);
      throw error;
    } finally {
      // A refused write resyncs too: the milestone may have been disabled or moved out of scope since the
      // list loaded, or first recorded by a concurrent request.
      await reloadData();
    }
  };

  const record = (milestoneKey: string, occurredOn: string) =>
    runMutation(() => recordClaimMilestone(claim.organization_id, claim.id, milestoneKey, { occurred_on: occurredOn }));

  const clear = (milestoneKey: string) =>
    runMutation(() => clearClaimMilestone(claim.organization_id, claim.id, milestoneKey));

  return { milestones: data?.milestones ?? [], isLoading, isError, record, clear };
};

export default useClaimMilestones;
